/* @flow */
import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { translate } from 'react-i18next';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import type { TranslatorProps } from 'react-i18next';

import { apiService } from 'services/ServiceRegistry';

import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';
import ElectroAssetFamilyItem from './ElectroAssetFamilyItem';
import ElectroMessageBarCont from 'forms/components/inputs/ElectroMessageBarCont/ElectroMessageBarCont';

import styles from './ElectroAssetFamily.module.scss';

/**
 * ElectroAssetFamily
 *
 * @prop {Object} field
 * @prop {String} assetId
 * @prop {String} heading
 * @return {ActionDrawerBlock}
 */
@translate()
@observer
export default class ElectroAssetFamily extends React.Component {
  @observable loading: boolean = false;

  asset: Object = {};

  parent: Object = {};

  children: Array<Object> = [];

  isParent: boolean = true;

  async loadOptions(endpoint: string): Promise<void> {
    this.loading = true;

    try {
      const response: * = await apiService.getResource(endpoint);

      this.handleOnLoad(response);
    } catch (err) {
      console.log(err);
    }

    this.loading = false;
  }

  get showSelectAll() {
    return (
      (this.isParent && this.children.length > 0) || (!this.isParent && this.children.length > 1)
    );
  }

  handleOnLoad = (response: *) => {
    const field: Object = this.props.field;

    if (response.id) {
      this.asset = {
        id: response.id,
        asset_number: response.asset_number,
        model: response.model,
        manufacturer: response.manufacturer,
      };

      if (response.is_child) {
        this.parent = response.family.parent;

        // response.family.children already includes this.asset
        // when current asset is a child, so the concat duplicates it
        // this.children = [this.asset].concat(response.family.children);
        this.children = response.family.children;

        this.isParent = false;
      } else {
        this.parent = this.asset;

        this.children = response.family.children;
      }

      field.value = [this.asset.id];
    }
  };

  handleOnCheckAllToggle = () => {
    const field: Object = this.props.field;

    if (field.value.length === this.children.length + 1) {
      field.set([this.asset.id]);
    } else {
      const arr: Array<string> = this.children.map((item: Object) => item.id);

      arr.unshift(this.parent.id);

      field.set(arr);
    }
  };

  handleOnItemClick = (value: string) => {
    if (value !== this.asset.id) {
      const field: Object = this.props.field;
      const index: number = field.value.indexOf(value);
      const arr: Array<string> = [...field.value];

      if (index === -1) {
        arr.push(value);
      } else {
        arr.splice(index, 1);
      }

      field.set(arr);
    }
  };

  checkWarning() {
    const field: Object = this.props.field;

    // Show warning if not all assets are Selected
    if (field.value.length !== this.children.length + 1) {
      return true;
    }

    return false;
  }

  componentDidMount() {
    const assetId: Object = this.props.assetId;

    if (assetId) {
      this.loadOptions(`assets/${assetId}/change-asset-location`);
    }
  }

  render(): ActionDrawerBlock {
    const {
      field,
      heading,
      assetId,
      t,
    }: {
      field: Object,
      heading: string,
      assetId: string,
      t: TranslatorProps,
    } = this.props;

    if (this.loading || (this.children && this.children.length === 0)) {
      return null;
    }

    return (
      <ActionDrawerBlock gutters={true} divider={true}>
        {heading && <h3>{heading}</h3>}
        {!this.loading && (
          <div className={styles['asset-family']}>
            {this.showSelectAll && (
              <div className={styles['select-all']}>
                <FormControlLabel
                  label={t('general:actions.select_all')}
                  labelPlacement="start"
                  control={
                    <Checkbox
                      disabled={this.loading}
                      checked={field.value.length === this.children.length + 1}
                      disableRipple={true}
                      tabIndex={-1}
                      onClick={this.handleOnCheckAllToggle}
                    />
                  }
                />
              </div>
            )}
            <h5>{t('general:assets.parent_asset_plural')}:</h5>
            <div className={styles['family-list']}>
              <List disablePadding={true}>
                <ElectroAssetFamilyItem
                  isChild={false}
                  key={this.parent.id}
                  item={this.parent}
                  items={field.value}
                  current={assetId}
                  handleOnClick={this.handleOnItemClick}
                />
              </List>
            </div>
            {this.children.length > 0 && (
              <React.Fragment>
                {this.checkWarning() && (
                  <ElectroMessageBarCont
                    props={{
                      variant: 'warning',
                      message: t('general:assets.messages.separation'),
                    }}
                  />
                )}
                <h5>{t('general:assets.child_asset_plural')}:</h5>
                <div className={styles['family-list']}>
                  <List disablePadding={true}>
                    {this.children.map(
                      (item: Object): ListItem => (
                        <ElectroAssetFamilyItem
                          isChild={true}
                          key={item.id}
                          item={item}
                          items={field.value}
                          current={assetId}
                          handleOnClick={this.handleOnItemClick}
                        />
                      )
                    )}
                  </List>
                </div>
              </React.Fragment>
            )}
            {field.error && field.showError && (
              <FormHelperText error={true}>{field.error}</FormHelperText>
            )}
          </div>
        )}
      </ActionDrawerBlock>
    );
  }
}
